import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, Shield, Send, Image, X, Loader2, UserPlus, UserMinus,
  MoreHorizontal, CheckCircle, Trash2, Settings, Crown, Lock, Globe,
  BadgeCheck, AlertTriangle, Wallet, Coins, FileText, ChevronDown,
  ChevronUp, Activity, MessageCircle, Pin, Heart, Tag, Pencil,
} from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useNip05Verified, useDisplayName } from '../hooks/useNip05Verified';
import { useTipWallet } from '../hooks/useTipWallet';
import { useBCHPrice, getBCHPrice } from '../hooks/useBCHPrice';
import { useBCHAddress } from '../hooks/useBCHAddress';
import { uploadMedia, uploadImage } from '../lib/imageUpload';
import PostCard from '../components/PostCard';
import VerifiedBadge from '../components/VerifiedBadge';
import BCHTipButton from '../components/BCHTipButton';
import { useCommunityPin } from '../hooks/usePinnedPost';
import { BCHLogo } from '../components/Layout';
import { OPERATOR_PUBKEY } from '../lib/platform';
import { timeAgo } from '../lib/utils';

import {
  COMMUNITY_DEFINITION_KIND,
  COMMUNITY_APPROVAL_KIND,
  COMMUNITY_POST_KIND,
  MEMBERSHIP_KIND,
  PLATFORM_WALLET,
  parseCommunity,
  parseMembership,
  buildCommunityPostTags,
  buildCommunityDefinitionTags,
  buildRemovalTags,
  buildMemberRemovalTags,
  buildMemberReinstateTags,
  membershipDTag,
  isModerator,
  isOwner,
  canJoinCommunity,
  calculateTipRevenueSplit,
  usdToSats,
  type Community,
  type CommunityType,
} from '../lib/communities';

// Relays known to index community events — query broadly for reliability.
// Posts "disappearing" was caused by too few relays and only waiting for
// the first EOSE. Including more relays gives a fuller picture.
const COMMUNITY_RELAYS = [
  'wss://relay.bchnostr.com',
  'wss://nos.lol',
  'wss://relay.primal.net',
  'wss://relay.nostr.net',
];

export default function CommunityViewPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const { user, setShowLoginDialog } = useAppContext();
  const navigate = useNavigate();
  const decodedId = communityId ? decodeURIComponent(communityId) : '';

  // Parse the community a-tag: "34550:<pubkey>:<d-tag>"
  const parts = decodedId.split(':');
  const creatorPubkey = parts[1] || '';
  const dTag = parts.slice(2).join(':') || '';

  // ── Tab state ──
  const [activeTab, setActiveTab] = useState<'posts' | 'chat' | 'about' | 'members'>('posts');

  // ── Fetch community definition ──
  const { events: defEvents, loading: defLoading } = useNostrQuery({
    filters: creatorPubkey && dTag ? [{ kinds: [COMMUNITY_DEFINITION_KIND], authors: [creatorPubkey], '#d': [dTag], limit: 5 }] : [],
    enabled: !!creatorPubkey && !!dTag,
    allRelays: true,
    extraRelays: COMMUNITY_RELAYS,
  });

  const community = useMemo<Community | null>(() => {
    if (defEvents.length === 0) return null;
    // Get latest version
    const sorted = [...defEvents].sort((a, b) => b.created_at - a.created_at);
    return parseCommunity(sorted[0]);
  }, [defEvents]);

  // ── Fetch members ──
  // Primary query: by community a-tag (finds all members)
  const { events: membershipEvents, refetch: refetchMembers } = useNostrQuery({
    filters: decodedId ? [{ kinds: [MEMBERSHIP_KIND], '#a': [decodedId], limit: 500 }] : [],
    enabled: !!decodedId,
    allRelays: true,
    extraRelays: COMMUNITY_RELAYS,
  });

  // Fallback query: fetch current user's membership by their d-tag directly.
  // Some relays don't index 'a' tags well, so this ensures we detect the
  // current user's membership even when the primary query misses it.
  const userMembershipDTag = decodedId ? membershipDTag(decodedId) : '';
  const { events: selfMembershipEvents } = useNostrQuery({
    filters: user && userMembershipDTag
      ? [
          // Self-published membership
          { kinds: [MEMBERSHIP_KIND], authors: [user.pubkey], '#d': [userMembershipDTag], limit: 5 },
          // Admin-granted membership (admin publishes with #p tag pointing to user)
          { kinds: [MEMBERSHIP_KIND], '#p': [user.pubkey], '#t': ['bch-community-grant'], limit: 20 },
        ]
      : [],
    enabled: !!user && !!userMembershipDTag,
  });

  // Also try to recover from a pending membership stored locally
  // (payment succeeded but publish failed on a previous attempt)
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);
  const { publish: recoverPublish } = useNostrPublish();
  React.useEffect(() => {
    if (!user || !decodedId || recoveryAttempted) return;
    setRecoveryAttempted(true);
    try {
      const raw = localStorage.getItem(`bchnostr:pending-membership:${decodedId}`);
      if (!raw) return;
      const pending = JSON.parse(raw);
      if (pending.pubkey !== user.pubkey) return;
      // Attempt to re-publish the membership event
      recoverPublish({
        kind: MEMBERSHIP_KIND,
        content: JSON.stringify({ paidSats: pending.paidSats, role: 'member', recovered: true }),
        tags: [
          ['d', membershipDTag(decodedId)],
          ['a', decodedId],
          ['t', 'bch-community-member'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      }).then(() => {
        localStorage.removeItem(`bchnostr:pending-membership:${decodedId}`);
        refetchMembers();
      }).catch(() => {});
    } catch {}
  }, [user, decodedId, recoveryAttempted]);

  const members = useMemo(() => {
    // Merge primary + fallback membership events
    const allEvents = [...membershipEvents, ...selfMembershipEvents];
    const seen = new Set<string>();
    const map = new Map<string, { pubkey: string; joinedAt: number; paidSats?: number }>();
    for (const ev of allEvents) {
      if (seen.has(ev.id)) continue;
      seen.add(ev.id);
      const m = parseMembership(ev);
      if (!m || m.communityId !== decodedId) continue;
      // Keep latest per pubkey
      const existing = map.get(m.pubkey);
      if (!existing || m.joinedAt > existing.joinedAt) {
        map.set(m.pubkey, { pubkey: m.pubkey, joinedAt: m.joinedAt, paidSats: m.paidSats });
      }
    }
    return [...map.values()].sort((a, b) => b.joinedAt - a.joinedAt);
  }, [membershipEvents, selfMembershipEvents, decodedId]);

  const memberPubkeys = useMemo(() => new Set(members.map(m => m.pubkey)), [members]);
  const isMember = user ? memberPubkeys.has(user.pubkey) : false;

  // ── Fetch posts ──
  // Only fetch kind 1111 (community posts) tagged with this community.
  // We query both uppercase A and lowercase a tags for compatibility.
  // Kind 1 notes are NOT fetched here — they were leaking unrelated posts
  // (replies, comments from other clients) into the community feed.
  const { events: postEvents, loading: postsLoading, refetch } = useNostrQuery({
    filters: decodedId ? [
      { kinds: [COMMUNITY_POST_KIND], '#A': [decodedId], limit: 300 },
      { kinds: [COMMUNITY_POST_KIND], '#a': [decodedId], limit: 300 },
    ] : [],
    enabled: !!decodedId,
    allRelays: true,
    extraRelays: COMMUNITY_RELAYS,
  });

  // ── Auto-refetch when page becomes visible (user returns from another tab) ──
  // This catches posts that arrived while the user was away, preventing
  // the "disappearing posts" perception.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && decodedId) {
        refetch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [decodedId, refetch]);

  // ── Fetch removals (mod actions) ──
  const { events: approvalEvents } = useNostrQuery({
    filters: decodedId ? [{ kinds: [COMMUNITY_APPROVAL_KIND], '#a': [decodedId], limit: 500 }] : [],
    enabled: !!decodedId && !!community,
    allRelays: true,
    extraRelays: COMMUNITY_RELAYS,
  });

  // Build removed post IDs and removed member pubkeys.
  // For members, both remove and reinstate events are timestamped —
  // the most recent action per pubkey wins.
  const { removedPostIds, removedMemberPubkeys } = useMemo(() => {
    const postIds = new Set<string>();
    const memberPks = new Set<string>();
    if (!community) return { removedPostIds: postIds, removedMemberPubkeys: memberPks };

    // Track the latest action per member pubkey: { action, timestamp }
    const memberActions = new Map<string, { action: string; ts: number }>();

    for (const ev of approvalEvents) {
      if (!isModerator(community, ev.pubkey)) continue;
      const action = ev.tags?.find((t: string[]) => t[0] === 'action')?.[1];
      if (action === 'remove') {
        const eid = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
        if (eid) postIds.add(eid);
      }
      if (action === 'remove-member' || action === 'reinstate-member') {
        const pk = ev.tags?.find((t: string[]) => t[0] === 'p')?.[1];
        if (pk) {
          const existing = memberActions.get(pk);
          // Most recent action wins (by event created_at)
          if (!existing || ev.created_at > existing.ts) {
            memberActions.set(pk, { action, ts: ev.created_at });
          }
        }
      }
    }

    // Only pubkeys whose latest action is 'remove-member' are removed
    for (const [pk, { action }] of memberActions) {
      if (action === 'remove-member') memberPks.add(pk);
    }

    return { removedPostIds: postIds, removedMemberPubkeys: memberPks };
  }, [approvalEvents, community]);

  // Deduplicate and sort posts, filter removed + non-members + replies
  const posts = useMemo(() => {
    const seen = new Set<string>();
    return postEvents
      .filter(e => {
        if (seen.has(e.id)) return false;
        if (removedPostIds.has(e.id)) return false;
        seen.add(e.id);
        // Trust boundary: hide posts only from authors a moderator has
        // explicitly removed. We deliberately do NOT gate on the membership
        // list here — that list is fetched from relays by the community
        // `a`-tag, and many relays don't index `a`/`#a` reliably, so it is
        // frequently incomplete. Gating on it silently dropped genuine
        // members' comments ("disappearing comments"). The post already
        // carries this community's `A`/`a` tag, so it is scoped correctly;
        // moderator removal is the real trust boundary.
        if (removedMemberPubkeys.has(e.pubkey)) return false;
        // Filter out replies — replies have an 'e' tag with 'reply' marker.
        // Top-level community posts should NOT have an 'e' tag.
        const isReply = e.tags?.some((t: string[]) => t[0] === 'e' && (t[3] === 'reply' || t[3] === 'root'));
        if (isReply) return false;
        return true;
      })
      .sort((a, b) => b.created_at - a.created_at);
  }, [postEvents, removedPostIds, removedMemberPubkeys, community]);

  // Filter out removed members
  const activeMembers = useMemo(() =>
    members.filter(m => !removedMemberPubkeys.has(m.pubkey)),
  [members, removedMemberPubkeys]);

  // Removed members (for mod reinstate UI)
  const removedMembers = useMemo(() =>
    members.filter(m => removedMemberPubkeys.has(m.pubkey)),
  [members, removedMemberPubkeys]);

  const isUserMod = user && community ? isModerator(community, user.pubkey) : false;
  const isUserOwner = user && community ? isOwner(community, user.pubkey) : false;
  const isUserRemovedMember = user ? removedMemberPubkeys.has(user.pubkey) : false;

  // ── Pinned post ──
  const modPubkeys = useMemo(() => community?.moderators || [], [community]);
  const { pinnedEventId: communityPinnedId, pin: communityPin, unpin: communityUnpin } = useCommunityPin(decodedId || undefined, modPubkeys);
  const pinnedPost = useMemo(() => {
    if (!communityPinnedId) return null;
    return posts.find(p => p.id === communityPinnedId) || postEvents.find(p => p.id === communityPinnedId) || null;
  }, [communityPinnedId, posts, postEvents]);

  // Can the user participate? (verified, member or mod, not removed)
  const canParticipate = isMember && !isUserRemovedMember;

  if (defLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!community) {
    return (
      <div className="text-center py-20 px-4">
        <Users className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
        <p className="text-sm font-medium">Community not found</p>
        <Link to="/communities" className="text-xs text-primary hover:underline mt-2 inline-block">Browse communities</Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate('/communities')} className="p-1.5 rounded-lg hover:bg-muted shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="font-bold text-base truncate">{community.name}</h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{activeMembers.length} member{activeMembers.length !== 1 ? 's' : ''}</span>
              <span>·</span>
              <span>{posts.length} post{posts.length !== 1 ? 's' : ''}</span>
              {community.type === 'premium' && (
                <>
                  <span>·</span>
                  <span className="text-amber-600 dark:text-amber-400 font-bold">${community.joinFeeUsd} to join</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {isUserOwner && (
              <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                <Crown className="w-3 h-3" /> Owner
              </span>
            )}
            {isUserMod && !isUserOwner && (
              <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary/10 text-primary border border-primary/20">
                <Shield className="w-3 h-3" /> Mod
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Banner / Info */}
      <div className="border-b border-border">
        {community.image && (
          <img src={community.image} alt="" className="w-full h-36 object-cover" />
        )}
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-start gap-3">
            {community.image ? (
              <img src={community.image} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0 -mt-8 ring-4 ring-background" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Users className="w-7 h-7 text-primary/60" />
              </div>
            )}
            <div className="flex-1 min-w-0 pt-1">
              <div className="flex items-center gap-2">
                {community.type === 'premium' ? (
                  <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                    <Lock className="w-2.5 h-2.5" /> Premium
                  </span>
                ) : (
                  <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    <Globe className="w-2.5 h-2.5" /> Open
                  </span>
                )}
              </div>
              {community.description && (
                <p className="text-sm text-muted-foreground mt-1">{community.description}</p>
              )}
            </div>
          </div>

          {/* Join / Membership status */}
          <JoinSection
            community={community}
            isMember={isMember}
            isUserMod={isUserMod || false}
            isUserRemovedMember={isUserRemovedMember}
            onJoined={refetchMembers}
          />
        </div>
      </div>

      {/* Tab bar */}
      <div className="sticky top-[57px] z-[9] bg-background/90 backdrop-blur-md border-b border-border flex">
        {(['posts', 'chat', 'about', 'members'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2.5 text-xs font-bold capitalize transition-colors border-b-2 ${
              activeTab === tab
                ? 'text-primary border-primary'
                : 'text-muted-foreground border-transparent hover:text-foreground'
            }`}
          >
            {tab === 'posts' && <><MessageCircle className="w-3.5 h-3.5 inline mr-1" />Posts ({posts.length})</>}
            {tab === 'chat' && <><MessageCircle className="w-3.5 h-3.5 inline mr-1" />Chat</>}
            {tab === 'about' && <><FileText className="w-3.5 h-3.5 inline mr-1" />About</>}
            {tab === 'members' && <><Users className="w-3.5 h-3.5 inline mr-1" />Members ({activeMembers.length})</>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'posts' && (
        <div>
          {/* Compose */}
          {canParticipate ? (
            <CommunityComposer community={community} onPublished={refetch} />
          ) : !user ? (
            <div className="border-b border-border p-4 text-center">
              <p className="text-sm text-muted-foreground">
                <button onClick={() => setShowLoginDialog(true)} className="text-primary hover:underline font-medium">Log in</button> to post in this community.
              </p>
            </div>
          ) : !isMember ? (
            <div className="border-b border-border p-4 text-center">
              <p className="text-sm text-muted-foreground">Join this community to post.</p>
            </div>
          ) : null}

          {/* Posts */}
          {postsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16 px-4">
              <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground/20 mb-2" />
              <p className="text-sm text-muted-foreground">No posts yet. Be the first!</p>
            </div>
          ) : (
            <div>
              {/* Pinned post (always first) */}
              {pinnedPost && (
                <div key={`pinned-${pinnedPost.id}`} className="relative border-b-2 border-primary/20">
                  <PostCard
                    event={pinnedPost}
                    isPinned
                    onUnpin={isUserMod ? () => communityUnpin() : undefined}
                  />
                  {isUserMod && (
                    <PostModActions community={community} post={pinnedPost} onAction={refetch} isPinned onUnpin={communityUnpin} />
                  )}
                </div>
              )}
              {/* Regular posts (exclude pinned to avoid duplicate) */}
              {posts.filter(p => p.id !== communityPinnedId).map(post => (
                <div key={post.id} className="relative">
                  <PostCard event={post} />
                  {/* Mod actions */}
                  {isUserMod && (
                    <PostModActions community={community} post={post} onAction={refetch} onPin={() => communityPin(post.id)} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'chat' && (
        <CommunityChat
          community={community}
          canParticipate={canParticipate}
          memberPubkeys={memberPubkeys}
        />
      )}

      {activeTab === 'about' && (
        <AboutTab
          community={community}
          isUserOwner={isUserOwner || false}
          onUpdated={() => {}}
        />
      )}

      {activeTab === 'members' && (
        <MembersTab
          community={community}
          members={activeMembers}
          removedMembers={removedMembers}
          isUserMod={isUserMod || false}
          isUserOwner={isUserOwner || false}
          isAdmin={user?.pubkey === OPERATOR_PUBKEY}
          removedMemberPubkeys={removedMemberPubkeys}
          onMemberAdded={refetchMembers}
        />
      )}
    </div>
  );
}

// ── Join Section ─────────────────────────────────────────────────

function JoinSection({
  community,
  isMember,
  isUserMod,
  isUserRemovedMember,
  onJoined,
}: {
  community: Community;
  isMember: boolean;
  isUserMod: boolean;
  isUserRemovedMember: boolean;
  onJoined: () => void;
}) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const { price: bchPrice } = useBCHPrice();
  const { profile } = useNostrProfile(user?.pubkey || '');
  const nip05Value = typeof profile?.nip05 === 'string' ? profile.nip05 : '';
  const isVerified = useNip05Verified(nip05Value, user?.pubkey);
  const { address: ownerAddress } = useBCHAddress(community.creator);

  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [walletInitializing, setWalletInitializing] = useState(false);

  const eligibility = canJoinCommunity(isVerified);

  if (!user) {
    return (
      <button
        onClick={() => setShowLoginDialog(true)}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
      >
        <UserPlus className="w-4 h-4" /> Log in to Join
      </button>
    );
  }

  if (isUserRemovedMember) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-600 dark:text-red-400">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        You have been removed from this community.
      </div>
    );
  }

  if (isMember || isUserMod) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-600 dark:text-emerald-400">
        <CheckCircle className="w-4 h-4 shrink-0" />
        <span className="font-medium">You're a member of this community</span>
      </div>
    );
  }

  // Check for a pending membership (paid but membership event not published)
  const hasPendingMembership = (() => {
    try {
      const raw = localStorage.getItem(`bchnostr:pending-membership:${community.id}`);
      if (!raw) return false;
      const pending = JSON.parse(raw);
      return pending.pubkey === user?.pubkey;
    } catch { return false; }
  })();

  // Show recovery banner prominently if payment was made but membership failed
  if (hasPendingMembership) {
    return (
      <div className="space-y-2">
        <div className="flex items-start gap-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 p-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-bold text-amber-700 dark:text-amber-400">Payment received — finalizing membership</p>
            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
              Your BCH payment went through but the membership confirmation didn't reach the network. Tap below to complete your membership — you will <strong className="text-foreground">not</strong> be charged again.
            </p>
          </div>
        </div>
        <button
          onClick={handleJoin}
          disabled={joining}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          {joining ? 'Confirming...' : 'Complete Membership (no charge)'}
        </button>
        {error && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-2">
            <p className="text-[10px] text-red-500">{error}</p>
          </div>
        )}
      </div>
    );
  }

  if (!eligibility.eligible) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-400">
          <BadgeCheck className="w-4 h-4 shrink-0" />
          {eligibility.reason}
        </div>
        <a
          href="/settings?tab=verification"
          className="block text-center text-xs text-primary hover:underline"
        >
          Get verified →
        </a>
      </div>
    );
  }

  // Helper: publish membership event with retries
  const publishMembership = async (contentObj: Record<string, any>) => {
    const membershipEvent = {
      kind: MEMBERSHIP_KIND,
      content: JSON.stringify(contentObj),
      tags: [
        ['d', membershipDTag(community.id)],
        ['a', community.id],
        ['t', 'bch-community-member'],
      ],
      created_at: Math.floor(Date.now() / 1000),
    };

    // Try up to 3 times — relay publish can be flaky
    let lastErr: any;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await publish(membershipEvent);
        return; // success
      } catch (err) {
        lastErr = err;
        if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
    throw lastErr;
  };

  // Can join — free or paid
  const handleJoin = async () => {
    if (!user) return;
    setJoining(true);
    setError('');

    try {
      // Check if user already paid (pending membership from a failed publish).
      // If so, skip payment and just publish the membership event.
      if (hasPendingMembership) {
        try {
          const raw = localStorage.getItem(`bchnostr:pending-membership:${community.id}`);
          const pending = raw ? JSON.parse(raw) : null;
          await publishMembership({ paidSats: pending?.paidSats || 0, role: 'member', recovered: true });
          localStorage.removeItem(`bchnostr:pending-membership:${community.id}`);
          onJoined();
          return;
        } catch (recoverErr) {
          setError('Recovery failed — please try again.');
          setJoining(false);
          return;
        }
      }

      // Premium: pay the join fee — 80% to community owner, 20% to platform
      if (community.type === 'premium' && community.joinFeeUsd > 0) {
        if (!tipWallet.ready) await tipWallet.init();

        const totalSats = usdToSats(community.joinFeeUsd, bchPrice);
        if (!totalSats) throw new Error('Could not calculate BCH price');

        const { ownerSats, platformSats } = calculateTipRevenueSplit(totalSats);
        const effectiveOwnerAddress = community.ownerAddress || (ownerAddress ? ownerAddress : null);

        // ── Step 1: Send payments ──
        // Send owner's 80% (or to platform if no owner address)
        const ownerTarget = effectiveOwnerAddress || PLATFORM_WALLET;
        if (ownerSats >= 546) {
          await tipWallet.sendBCH(ownerTarget, BigInt(ownerSats));
        }

        // Send platform's 20% — wrapped in try/catch so a failure here
        // does NOT block the membership event from being published.
        // The user already paid the owner fee, so they deserve membership.
        try {
          if (platformSats >= 546 && effectiveOwnerAddress) {
            await tipWallet.sendBCH(PLATFORM_WALLET, BigInt(platformSats));
          }
        } catch (feeErr) {
          console.warn('Platform fee failed (membership will still be granted):', feeErr);
        }

        // ── Step 2: ALWAYS publish membership event after payment ──
        // This is the critical step — if payment succeeded, membership
        // must be recorded. Retries ensure relay flakiness doesn't
        // leave the user locked out after paying.
        try {
          await publishMembership({ paidSats: totalSats, role: 'member' });
        } catch (pubErr) {
          // Payment went through but membership publish failed after retries.
          // Store proof locally so user can recover.
          try {
            localStorage.setItem(
              `bchnostr:pending-membership:${community.id}`,
              JSON.stringify({ communityId: community.id, paidSats: totalSats, ts: Date.now(), pubkey: user.pubkey })
            );
          } catch {}
          throw new Error(
            'Your BCH payment went through! But the membership confirmation didn\'t reach the network. ' +
            'Don\'t worry — your payment is saved. Please refresh the page and tap "Complete Membership" to finish joining. You won\'t be charged again.'
          );
        }

        tipWallet.refreshBalance();
      } else {
        // Open community: just publish membership event
        await publishMembership({ role: 'member' });
      }

      onJoined();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join');
    } finally {
      setJoining(false);
    }
  };

  const joinFeeSats = community.type === 'premium' ? usdToSats(community.joinFeeUsd, bchPrice) : null;

  return (
    <div className="space-y-2">
      {community.type === 'premium' && !tipWallet.ready && (
        <button
          onClick={async () => { setWalletInitializing(true); try { await tipWallet.init(); } catch {} setWalletInitializing(false); }}
          disabled={walletInitializing}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-muted text-sm font-medium hover:bg-muted/80 transition-colors"
        >
          {walletInitializing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
          {walletInitializing ? 'Loading wallet...' : 'Initialize Wallet to Join'}
        </button>
      )}

      <button
        onClick={handleJoin}
        disabled={joining || (community.type === 'premium' && !tipWallet.ready)}
        className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors disabled:opacity-50 ${
          community.type === 'premium'
            ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:opacity-90'
            : 'bg-primary text-primary-foreground hover:bg-primary/90'
        }`}
      >
        {joining ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <UserPlus className="w-4 h-4" />
        )}
        {joining
          ? 'Joining...'
          : community.type === 'premium'
            ? `Join for $${community.joinFeeUsd}${joinFeeSats ? ` (~${joinFeeSats.toLocaleString()} sats)` : ''}`
            : 'Join Community'
        }
      </button>
      {error && <p className="text-xs text-red-500 text-center">{error}</p>}
    </div>
  );
}

// ── Community Composer ───────────────────────────────────────────

// ── Content Labels (NIP-32) ─────────────────────────────────────
// Predefined labels for community posts. Users can self-label their
// posts to categorize them. The Activities page aggregates all posts
// labeled "ACTIVITY" across all community spaces.
const CONTENT_LABELS = [
  { value: 'ACTIVITY', label: 'Activity', emoji: '\u{1F3AF}', description: 'Community activity or challenge' },
  { value: 'DISCUSSION', label: 'Discussion', emoji: '\u{1F4AC}', description: 'General discussion' },
  { value: 'QUESTION', label: 'Question', emoji: '\u{2753}', description: 'Ask the community' },
  { value: 'ANNOUNCEMENT', label: 'Announcement', emoji: '\u{1F4E2}', description: 'Important update' },
  { value: 'SHOWCASE', label: 'Showcase', emoji: '\u{2728}', description: 'Share your work' },
] as const;

export const ACTIVITY_LABEL = 'ACTIVITY';
export const LABEL_NAMESPACE = 'bchnostr.content';

function CommunityComposer({ community, onPublished }: { community: Community; onPublished: () => void }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [content, setContent] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [attachments, setAttachments] = useState<{ url: string; type: 'image' | 'video' }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [showLabels, setShowLabels] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handlePublish = async () => {
    if (!user || (!content.trim() && attachments.length === 0)) return;
    setPublishing(true);
    try {
      const tags = buildCommunityPostTags(community);

      // Add content label (NIP-32 self-labeling)
      if (selectedLabel) {
        tags.push(['L', LABEL_NAMESPACE]);
        tags.push(['l', selectedLabel, LABEL_NAMESPACE]);
      }

      attachments.forEach(att => {
        if (att.type === 'video') {
          tags.push(['imeta', `url ${att.url}`, 'm video/mp4']);
        } else {
          tags.push(['imeta', `url ${att.url}`]);
        }
      });

      const mediaUrls = attachments.map(a => a.url).join('\n');
      const fullContent = mediaUrls ? (content.trim() ? content + '\n\n' + mediaUrls : mediaUrls) : content;

      await publish({
        kind: COMMUNITY_POST_KIND,
        content: fullContent,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      setContent('');
      setAttachments([]);
      setSelectedLabel(null);
      setShowLabels(false);
      onPublished();
    } catch (e) {
      console.error('Community post failed:', e);
    } finally {
      setPublishing(false);
    }
  };

  const activeLabelInfo = selectedLabel ? CONTENT_LABELS.find(l => l.value === selectedLabel) : null;

  return (
    <div className="border-b border-border p-4">
      <div className="flex gap-3">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <BCHLogo size={18} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder={`Post in ${community.name}...`}
            className="w-full bg-transparent resize-none text-sm placeholder:text-muted-foreground focus:outline-none min-h-[50px] max-h-[150px]"
            rows={2}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handlePublish();
              }
            }}
          />

          {/* Content label badge */}
          {activeLabelInfo && (
            <div className="flex items-center gap-1.5 mb-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold border border-primary/20">
                <Tag className="w-2.5 h-2.5" />
                {activeLabelInfo.emoji} {activeLabelInfo.label}
              </span>
              <button onClick={() => { setSelectedLabel(null); setShowLabels(false); }} className="text-muted-foreground hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Label picker */}
          {showLabels && (
            <div className="flex flex-wrap gap-1.5 mb-2 p-2 rounded-lg bg-muted/50 border border-border">
              <p className="w-full text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-0.5">Content Label</p>
              {CONTENT_LABELS.map(l => (
                <button
                  key={l.value}
                  onClick={() => { setSelectedLabel(l.value === selectedLabel ? null : l.value); setShowLabels(false); }}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                    selectedLabel === l.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background border border-border text-muted-foreground hover:text-foreground hover:border-primary/30'
                  }`}
                  title={l.description}
                >
                  <span>{l.emoji}</span> {l.label}
                </button>
              ))}
            </div>
          )}

          {/* Attachment previews */}
          {attachments.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {attachments.map((att, i) => (
                <div key={i} className="relative group">
                  {att.type === 'image' ? (
                    <img src={att.url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center text-[10px] text-muted-foreground">Video</div>
                  )}
                  <button
                    onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1">
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  e.target.value = '';
                  setUploading(true);
                  try {
                    const result = await uploadMedia(file);
                    setAttachments(prev => [...prev, { url: result.url, type: result.type === 'video' ? 'video' : 'image' }]);
                  } catch {}
                  setUploading(false);
                }}
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setShowLabels(!showLabels)}
                className={`p-1.5 rounded-lg transition-colors ${
                  selectedLabel ? 'text-primary bg-primary/5' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
                }`}
                title="Add content label"
              >
                <Tag className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handlePublish}
              disabled={publishing || (!content.trim() && attachments.length === 0)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Post
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── About Tab ────────────────────────────────────────────────────

function AboutTab({ community, isUserOwner, onUpdated }: { community: Community; isUserOwner: boolean; onUpdated: () => void }) {
  const { profile: ownerProfile } = useNostrProfile(community.creator);
  const ownerName = ownerProfile?.display_name || ownerProfile?.name || community.creator.slice(0, 8) + '...';
  const [showEditModal, setShowEditModal] = useState(false);

  return (
    <div className="px-4 py-4 space-y-4">
      {/* Owner */}
      <div className="space-y-1.5">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Community Owner</h3>
        <Link to={`/profile/${community.creator}`} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 transition-colors">
          {ownerProfile?.picture ? (
            <img src={ownerProfile.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Crown className="w-4 h-4 text-amber-500" />
            </div>
          )}
          <span className="text-sm font-medium">{ownerName}</span>
          <VerifiedBadge nip05={ownerProfile?.nip05} pubkey={community.creator} size="xs" />
          <Crown className="w-3.5 h-3.5 text-amber-500 ml-auto" />
        </Link>
      </div>

      {/* Description */}
      {community.description && (
        <div className="space-y-1.5">
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Description</h3>
          <p className="text-sm text-foreground leading-relaxed">{community.description}</p>
        </div>
      )}

      {/* Rules */}
      {community.rules && (
        <div className="space-y-1.5">
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Rules</h3>
          <div className="rounded-xl border border-border bg-muted/30 p-3">
            <p className="text-xs text-foreground leading-relaxed whitespace-pre-line">{community.rules}</p>
          </div>
        </div>
      )}

      {/* Community Type */}
      <div className="space-y-1.5">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Type</h3>
        <div className="flex items-center gap-2">
          {community.type === 'premium' ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <Lock className="w-4 h-4 text-amber-500" />
              <div>
                <p className="text-xs font-bold text-amber-600 dark:text-amber-400">Premium Community</p>
                <p className="text-[10px] text-muted-foreground">${community.joinFeeUsd} BCH to join</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <Globe className="w-4 h-4 text-emerald-500" />
              <div>
                <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400">Open Community</p>
                <p className="text-[10px] text-muted-foreground">Free for all verified users</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Moderators */}
      <div className="space-y-1.5">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Moderators</h3>
        <div className="space-y-1">
          {community.moderators.map(pk => (
            <ModeratorChip key={pk} pubkey={pk} isCreator={pk === community.creator} />
          ))}
        </div>
      </div>

      {/* Owner actions */}
      {isUserOwner && (
        <div className="pt-2 border-t border-border">
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-muted text-sm font-medium hover:bg-muted/80 transition-colors w-full justify-center"
          >
            <Settings className="w-4 h-4" /> Edit Community Settings
          </button>
        </div>
      )}

      {showEditModal && (
        <EditCommunityModal community={community} onClose={() => setShowEditModal(false)} onUpdated={onUpdated} />
      )}
    </div>
  );
}

// ── Members Tab ──────────────────────────────────────────────────

function MembersTab({
  community,
  members,
  removedMembers,
  isUserMod,
  isUserOwner,
  isAdmin,
  removedMemberPubkeys,
  onMemberAdded,
}: {
  community: Community;
  members: { pubkey: string; joinedAt: number }[];
  removedMembers: { pubkey: string; joinedAt: number }[];
  isUserMod: boolean;
  isUserOwner: boolean;
  isAdmin: boolean;
  removedMemberPubkeys: Set<string>;
  onMemberAdded: () => void;
}) {
  const canGrant = isUserOwner || isAdmin;

  return (
    <div className="px-4 py-4 space-y-1">
      {canGrant && (
        <GrantMembershipForm community={community} onGranted={onMemberAdded} isAdmin={isAdmin} />
      )}
      {members.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-10 h-10 mx-auto text-muted-foreground/20 mb-2" />
          <p className="text-sm text-muted-foreground">No members yet</p>
        </div>
      ) : (
        members.map(m => (
          <MemberRow
            key={m.pubkey}
            pubkey={m.pubkey}
            joinedAt={m.joinedAt}
            isMod={isModerator(community, m.pubkey)}
            isCreator={m.pubkey === community.creator}
            canModerate={isUserMod && m.pubkey !== community.creator}
            canManageMods={isUserOwner && m.pubkey !== community.creator}
            community={community}
          />
        ))
      )}

      {/* Removed Members — visible to mods so they can reinstate */}
      {isUserMod && removedMembers.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <h3 className="text-xs font-bold text-red-500/80 flex items-center gap-1.5 mb-2">
            <UserMinus className="w-3.5 h-3.5" />
            Removed Members ({removedMembers.length})
          </h3>
          <div className="space-y-1">
            {removedMembers.map(m => (
              <RemovedMemberRow
                key={m.pubkey}
                pubkey={m.pubkey}
                joinedAt={m.joinedAt}
                community={community}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Grant Membership Form ────────────────────────────────────────

function GrantMembershipForm({ community, onGranted, isAdmin }: { community: Community; onGranted: () => void; isAdmin: boolean }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [showForm, setShowForm] = useState(false);
  const [input, setInput] = useState('');
  const [granting, setGranting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleGrant = async () => {
    if (!user || !input.trim()) return;
    setGranting(true);
    setError('');
    setSuccess('');

    try {
      let targetPubkey = input.trim();

      // Decode npub
      if (targetPubkey.startsWith('npub1')) {
        try {
          const decoded = nip19.decode(targetPubkey);
          if (decoded.type !== 'npub') throw new Error('Invalid npub');
          targetPubkey = decoded.data as string;
        } catch {
          throw new Error('Invalid npub. Please enter a valid npub or hex pubkey.');
        }
      }

      if (!/^[0-9a-f]{64}$/i.test(targetPubkey)) {
        throw new Error('Invalid pubkey. Enter an npub (npub1...) or 64-char hex key.');
      }

      // Publish an admin-grant membership event.
      // Uses a distinct d-tag so it doesn't collide with the user's own
      // self-published membership, and includes a 'p' tag pointing to the
      // target user so the membership parser can pick it up.
      await publish({
        kind: MEMBERSHIP_KIND,
        content: JSON.stringify({
          role: 'member',
          grantedBy: user.pubkey,
          grantedFor: targetPubkey,
          reason: isAdmin ? 'Platform admin grant' : 'Community owner grant',
        }),
        tags: [
          ['d', membershipDTag(community.id) + ':grant:' + targetPubkey],
          ['a', community.id],
          ['p', targetPubkey],
          ['t', 'bch-community-member'],
          ['t', 'bch-community-grant'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      setSuccess('Membership granted!');
      setInput('');
      onGranted();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to grant membership');
    } finally {
      setGranting(false);
    }
  };

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 rounded-xl border-2 border-dashed border-border hover:border-primary/40 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
      >
        <UserPlus className="w-4 h-4" /> Add Member
      </button>
    );
  }

  return (
    <div className="mb-4 p-4 rounded-xl border border-border bg-muted/30 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold flex items-center gap-1.5">
          <UserPlus className="w-3.5 h-3.5 text-primary" /> Grant Membership
          {isAdmin && (
            <span className="px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[9px] font-bold border border-amber-500/20">Admin</span>
          )}
        </h3>
        <button onClick={() => { setShowForm(false); setError(''); setSuccess(''); }} className="p-1 rounded hover:bg-muted">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Add a user directly — they won't need to pay the join fee.
      </p>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="npub1... or hex pubkey"
          className="flex-1 bg-background rounded-lg px-3 py-2 text-xs font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          onKeyDown={e => { if (e.key === 'Enter') handleGrant(); }}
        />
        <button
          onClick={handleGrant}
          disabled={granting || !input.trim()}
          className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center gap-1.5"
        >
          {granting ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
          Grant
        </button>
      </div>
      {error && <p className="text-[11px] text-red-500">{error}</p>}
      {success && <p className="text-[11px] text-emerald-500 font-medium">{success}</p>}
    </div>
  );
}

function MemberRow({
  pubkey,
  joinedAt,
  isMod,
  isCreator,
  canModerate,
  canManageMods,
  community,
}: {
  pubkey: string;
  joinedAt: number;
  isMod: boolean;
  isCreator: boolean;
  canModerate: boolean;
  canManageMods: boolean;
  community: Community;
}) {
  const { profile } = useNostrProfile(pubkey);
  const { publish } = useNostrPublish();
  const name = useDisplayName(pubkey, profile, pubkey.slice(0, 8) + '...');
  const [showMenu, setShowMenu] = useState(false);
  const [acting, setActing] = useState(false);

  const handleRemoveMember = async () => {
    if (!confirm(`Remove ${name} from this community?`)) return;
    setActing(true);
    try {
      const tags = buildMemberRemovalTags(community, pubkey);
      await publish({
        kind: COMMUNITY_APPROVAL_KIND,
        content: '',
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch (e) {
      console.error('Remove member failed:', e);
    }
    setActing(false);
    setShowMenu(false);
  };

  const handleToggleMod = async () => {
    // Re-publish community definition with updated moderator list
    if (!community.raw) return;
    setActing(true);
    try {
      const currentMods = community.moderators.filter(pk => pk !== community.creator);
      let newMods: string[];
      if (isMod) {
        newMods = currentMods.filter(pk => pk !== pubkey);
      } else {
        newMods = [...currentMods, pubkey];
      }

      const tags = buildCommunityDefinitionTags({
        dTag: community.dTag,
        name: community.name,
        description: community.description,
        rules: community.rules,
        image: community.image || undefined,
        creatorPubkey: community.creator,
        moderators: newMods,
        type: community.type,
        joinFeeUsd: community.joinFeeUsd || undefined,
        ownerAddress: community.ownerAddress || undefined,
      });

      await publish({
        kind: COMMUNITY_DEFINITION_KIND,
        content: '',
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch (e) {
      console.error('Toggle mod failed:', e);
    }
    setActing(false);
    setShowMenu(false);
  };

  const timeStr = new Date(joinedAt * 1000).toLocaleDateString();

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors">
      <Link to={`/profile/${pubkey}`} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
            <Users className="w-4 h-4 text-primary/60" />
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <Link to={`/profile/${pubkey}`} className="text-sm font-medium truncate hover:text-primary transition-colors">{name}</Link>
          <VerifiedBadge nip05={profile?.nip05} pubkey={pubkey} size="xs" />
          {isCreator && <Crown className="w-3 h-3 text-amber-500 shrink-0" />}
          {isMod && !isCreator && <Shield className="w-3 h-3 text-primary shrink-0" />}
        </div>
        <p className="text-[10px] text-muted-foreground">Joined {timeStr}</p>
      </div>

      {(canModerate || canManageMods) && (
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-full mt-1 z-30 bg-popover border border-border rounded-lg shadow-lg overflow-hidden min-w-[160px]">
                {canManageMods && (
                  <button
                    onClick={handleToggleMod}
                    disabled={acting}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors text-left"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    {isMod ? 'Remove Moderator' : 'Make Moderator'}
                  </button>
                )}
                {canModerate && (
                  <button
                    onClick={handleRemoveMember}
                    disabled={acting}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors text-left text-red-500"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Remove Member
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Removed Member Row (with reinstate button) ───────────────────

function RemovedMemberRow({
  pubkey,
  joinedAt,
  community,
}: {
  pubkey: string;
  joinedAt: number;
  community: Community;
}) {
  const { profile } = useNostrProfile(pubkey);
  const { publish } = useNostrPublish();
  const name = useDisplayName(pubkey, profile, pubkey.slice(0, 8) + '...');
  const [reinstating, setReinstating] = useState(false);
  const [reinstated, setReinstated] = useState(false);

  const handleReinstate = async () => {
    if (!confirm(`Reinstate ${name} as a member of this community?`)) return;
    setReinstating(true);
    try {
      const tags = buildMemberReinstateTags(community, pubkey);
      await publish({
        kind: COMMUNITY_APPROVAL_KIND,
        content: '',
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });
      setReinstated(true);
    } catch (e) {
      console.error('Reinstate member failed:', e);
    }
    setReinstating(false);
  };

  if (reinstated) {
    return (
      <div className="flex items-center gap-3 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
        <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">{name} has been reinstated</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg bg-red-500/5 border border-red-500/10">
      <Link to={`/profile/${pubkey}`} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-9 h-9 rounded-full object-cover opacity-60" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
            <Users className="w-4 h-4 text-muted-foreground/40" />
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/profile/${pubkey}`} className="text-sm font-medium truncate text-muted-foreground hover:text-foreground transition-colors block">{name}</Link>
        <p className="text-[10px] text-red-500/70">Removed</p>
      </div>
      <button
        onClick={handleReinstate}
        disabled={reinstating}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-medium hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
      >
        {reinstating ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
        Reinstate
      </button>
    </div>
  );
}

// ── Community Chat ────────────────────────────────────────────────
// NIP-28 kind 42 chat messages scoped to a community via its a-tag.
// Uses the community's a-tag as the channel identifier.

const CHAT_MESSAGE_KIND = 42;
const CHAT_HIDE_KIND = 43;

// Detect image URLs for inline preview
function extractChatImages(content: string): string[] {
  const urls: string[] = [];
  const parts = content.split(/\s+/);
  for (const p of parts) {
    if (/^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|avif)(\?.*)?$/i.test(p)) {
      urls.push(p);
    }
  }
  return urls;
}

function stripChatImages(content: string, imageUrls: string[]): string {
  let text = content;
  for (const url of imageUrls) {
    text = text.split(url).join('');
  }
  return text.trim();
}

function CommunityChat({
  community,
  canParticipate,
  memberPubkeys,
}: {
  community: Community;
  canParticipate: boolean;
  memberPubkeys: Set<string>;
}) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());

  const communityATagValue = `${COMMUNITY_DEFINITION_KIND}:${community.creator}:${community.dTag}`;

  // Fetch chat messages (kind 42) tagged to this community
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 7); // last 7 days
  const { events: chatEvents, refetch } = useNostrQuery({
    filters: [
      { kinds: [CHAT_MESSAGE_KIND], '#a': [communityATagValue], since, limit: 200 },
    ],
    enabled: true,
    allRelays: true,
    extraRelays: COMMUNITY_RELAYS,
  });

  // Fetch hidden messages (kind 43) from mods
  const { events: hideEvents } = useNostrQuery({
    filters: [
      { kinds: [CHAT_HIDE_KIND], '#a': [communityATagValue], since, limit: 100 },
    ],
    enabled: true,
  });

  const hiddenIds = useMemo(() => {
    const ids = new Set<string>();
    hideEvents.forEach(ev => {
      // Only honor hides from moderators
      if (!isModerator(community, ev.pubkey)) return;
      ev.tags.filter((t: string[]) => t[0] === 'e').forEach((t: string[]) => ids.add(t[1]));
    });
    return ids;
  }, [hideEvents, community]);

  // Track locally deleted messages (before relay propagates)
  const [localDeletedIds, setLocalDeletedIds] = useState<Set<string>>(new Set());

  // Sort, filter, and resolve edits in messages
  const messages = useMemo(() => {
    const seen = new Set<string>();
    // Build edit map: originalId → latest edit event
    const editMap = new Map<string, any>();
    for (const ev of chatEvents) {
      const editOf = ev.tags?.find((t: string[]) => t[0] === '-edit-of')?.[1];
      if (editOf) {
        const existing = editMap.get(editOf);
        if (!existing || ev.created_at > existing.created_at) {
          editMap.set(editOf, ev);
        }
      }
    }
    // IDs of edit events themselves (to exclude them from the timeline as standalone messages)
    const editEventIds = new Set<string>();
    for (const ev of editMap.values()) editEventIds.add(ev.id);

    return chatEvents
      .filter(ev => {
        if (seen.has(ev.id)) return false;
        if (hiddenIds.has(ev.id)) return false;
        if (localDeletedIds.has(ev.id)) return false;
        // Don't show edit events as separate messages
        if (editEventIds.has(ev.id)) return false;
        seen.add(ev.id);
        return true;
      })
      .map(ev => {
        // If this message has been edited, swap in the edited content
        const editEvent = editMap.get(ev.id);
        if (editEvent) {
          return { ...ev, content: editEvent.content, tags: [...ev.tags, ['-edit-of', ev.id]] };
        }
        return ev;
      })
      .sort((a, b) => a.created_at - b.created_at);
  }, [chatEvents, hiddenIds, localDeletedIds]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, shouldAutoScroll]);

  // Initial scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'instant' as any });
  }, []);

  // Track scroll position to determine if auto-scroll should apply
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 80;
    setShouldAutoScroll(atBottom);
  }, []);

  // Poll for new messages every 15s
  useEffect(() => {
    const timer = setInterval(refetch, 15000);
    return () => clearInterval(timer);
  }, [refetch]);

  // Image upload handler
  const handleImageUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setImages(prev => [...prev, url]);
    } catch (e) {
      console.error('Chat image upload failed:', e);
    } finally {
      setUploading(false);
    }
  }, []);

  // Clipboard paste for images
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItem = Array.from(items).find(
      item => item.kind === 'file' && item.type.startsWith('image/')
    );
    if (!imageItem) return;
    e.preventDefault();
    const file = imageItem.getAsFile();
    if (file) handleImageUpload(file);
  }, [handleImageUpload]);

  // Send message
  const handleSend = async () => {
    if (!user || (!message.trim() && images.length === 0)) return;
    setSending(true);
    try {
      const parts: string[] = [];
      if (message.trim()) parts.push(message.trim());
      images.forEach(url => parts.push(url));
      const content = parts.join('\n\n');

      const tags: string[][] = [
        ['a', communityATagValue],
        ['t', 'bch-community-chat'],
      ];
      images.forEach(url => tags.push(['imeta', `url ${url}`]));

      await publish({
        kind: CHAT_MESSAGE_KIND,
        content,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      setMessage('');
      setImages([]);
      setShouldAutoScroll(true);
      setTimeout(refetch, 500);
    } catch (e) {
      console.error('Chat send failed:', e);
    } finally {
      setSending(false);
    }
  };

  // Like a chat message
  const handleLike = useCallback(async (msgId: string, msgPubkey: string) => {
    if (!user || likedIds.has(msgId)) return;
    setLikedIds(prev => new Set(prev).add(msgId));
    try {
      await publish({
        kind: 7,
        content: '+',
        tags: [['e', msgId], ['p', msgPubkey], ['a', communityATagValue]],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch {
      setLikedIds(prev => { const next = new Set(prev); next.delete(msgId); return next; });
    }
  }, [user, likedIds, publish, communityATagValue]);

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 220px)' }}>
      {/* Messages area */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-3 py-3 space-y-1"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <MessageCircle className="w-12 h-12 text-muted-foreground/20 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No messages yet</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Be the first to say something!</p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const isMe = msg.pubkey === user?.pubkey;
            const prevMsg = i > 0 ? messages[i - 1] : null;
            const sameSender = prevMsg?.pubkey === msg.pubkey;
            const timeDiff = prevMsg ? msg.created_at - prevMsg.created_at : Infinity;
            const showHeader = !sameSender || timeDiff > 300; // 5 min gap = show header again

            return (
              <ChatMessage
                key={msg.id}
                event={msg}
                isMe={isMe}
                showHeader={showHeader}
                isLiked={likedIds.has(msg.id)}
                onLike={() => handleLike(msg.id, msg.pubkey)}
                isMod={isModerator(community, user?.pubkey || '')}
                communityATag={communityATagValue}
                onDeleted={(id) => setLocalDeletedIds(prev => new Set(prev).add(id))}
                onEdited={() => setTimeout(refetch, 500)}
              />
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* "New messages" indicator when scrolled up */}
      {!shouldAutoScroll && messages.length > 0 && (
        <button
          onClick={() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            setShouldAutoScroll(true);
          }}
          className="mx-auto -mt-10 mb-2 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium shadow-lg z-10 relative"
        >
          ↓ New messages
        </button>
      )}

      {/* Input area */}
      {canParticipate ? (
        <div className="border-t border-border bg-background p-3">
          {/* Hidden file input */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleImageUpload(file);
              e.target.value = '';
            }}
          />

          {/* Image preview strip */}
          {(images.length > 0 || uploading) && (
            <div className="flex gap-2 mb-2 overflow-x-auto">
              {images.map((url, i) => (
                <div key={i} className="relative shrink-0 group">
                  <img src={url} alt="" className="w-14 h-14 rounded-lg object-cover border border-border" />
                  <button
                    onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center shadow text-[10px]"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {uploading && (
                <div className="w-14 h-14 rounded-lg border-2 border-dashed border-primary/30 flex items-center justify-center shrink-0">
                  <Loader2 className="w-4 h-4 text-primary animate-spin" />
                </div>
              )}
            </div>
          )}

          <div className="flex items-end gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="shrink-0 p-2 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors mb-0.5"
              title="Attach image"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : <Image className="w-5 h-5" />}
            </button>
            <textarea
              ref={inputRef}
              value={message}
              onChange={e => setMessage(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              onInput={e => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 120) + 'px';
              }}
              placeholder="Type a message..."
              className="flex-1 bg-muted rounded-2xl px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none min-h-[42px] max-h-[120px]"
              rows={1}
            />
            <button
              onClick={handleSend}
              disabled={sending || (!message.trim() && images.length === 0)}
              className="shrink-0 p-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-30 transition-colors mb-0.5"
            >
              {sending
                ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <Send className="w-4 h-4" />
              }
            </button>
          </div>
        </div>
      ) : !user ? (
        <div className="border-t border-border p-4 text-center">
          <p className="text-sm text-muted-foreground">
            <button onClick={() => setShowLoginDialog(true)} className="text-primary hover:underline font-medium">Log in</button> to chat in this community.
          </p>
        </div>
      ) : (
        <div className="border-t border-border p-4 text-center">
          <p className="text-sm text-muted-foreground">Join this community to participate in chat.</p>
        </div>
      )}
    </div>
  );
}

// ── Chat Message Bubble ──────────────────────────────────────────

function ChatMessage({
  event,
  isMe,
  showHeader,
  isLiked,
  onLike,
  isMod,
  communityATag,
  onDeleted,
  onEdited,
}: {
  event: any;
  isMe: boolean;
  showHeader: boolean;
  isLiked: boolean;
  onLike: () => void;
  isMod: boolean;
  communityATag: string;
  onDeleted?: (id: string) => void;
  onEdited?: (id: string, newContent: string) => void;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile } = useNostrProfile(event.pubkey);
  const name = useDisplayName(event.pubkey, profile, event.pubkey.slice(0, 8));
  const [showActions, setShowActions] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  // Extract images from content
  const imageUrls = extractChatImages(event.content);
  const textContent = stripChatImages(event.content, imageUrls);

  // Hide message (mod action)
  const handleHide = async () => {
    if (!user) return;
    try {
      await publish({
        kind: CHAT_HIDE_KIND,
        content: JSON.stringify({ reason: 'Hidden by moderator' }),
        tags: [['e', event.id], ['a', communityATag]],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch {}
  };

  // Delete own message (NIP-09)
  const handleDelete = async () => {
    if (!user || !isMe) return;
    try {
      await publish({
        kind: 5,
        content: 'Deleted by author',
        tags: [['e', event.id], ['a', communityATag]],
        created_at: Math.floor(Date.now() / 1000),
      });
      setDeleted(true);
      onDeleted?.(event.id);
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  // Start editing
  const startEdit = () => {
    setEditContent(textContent);
    setEditing(true);
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  // Save edit — publish a new message with a reference to the original
  const handleSaveEdit = async () => {
    if (!user || !editContent.trim() || saving) return;
    setSaving(true);
    try {
      // Re-add images if they were in the original
      const parts = [editContent.trim()];
      imageUrls.forEach((url: string) => parts.push(url));
      const newContent = parts.join('\n\n');

      const tags: string[][] = [
        ['a', communityATag],
        ['t', 'bch-community-chat'],
        ['-edit-of', event.id],
      ];
      imageUrls.forEach((url: string) => tags.push(['imeta', `url ${url}`]));

      await publish({
        kind: CHAT_MESSAGE_KIND,
        content: newContent,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });
      setEditing(false);
      onEdited?.(event.id, newContent);
    } catch (e) {
      console.error('Edit failed:', e);
    }
    setSaving(false);
  };

  if (deleted) {
    return (
      <div className={`flex gap-2 ${showHeader ? 'mt-3' : 'mt-0.5'} ${isMe ? 'flex-row-reverse' : ''}`}>
        {showHeader ? <div className="w-8 shrink-0" /> : <div className="w-8 shrink-0" />}
        <div className={`flex-1 min-w-0 ${isMe ? 'flex flex-col items-end' : ''}`}>
          <div className="max-w-[85%] inline-block">
            <div className={`rounded-2xl px-3 py-2 ${isMe ? 'bg-primary/20 rounded-tr-md' : 'bg-muted/50 rounded-tl-md'}`}>
              <p className="text-xs italic text-muted-foreground flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Message deleted
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`group flex gap-2 ${showHeader ? 'mt-3' : 'mt-0.5'} ${isMe ? 'flex-row-reverse' : ''}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onTouchStart={() => setShowActions(true)}
    >
      {/* Avatar — only on header rows */}
      {showHeader ? (
        <Link to={`/profile/${event.pubkey}`} className="shrink-0 mt-0.5">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="w-4 h-4 text-primary/50" />
            </div>
          )}
        </Link>
      ) : (
        <div className="w-8 shrink-0" /> 
      )}

      <div className={`flex-1 min-w-0 ${isMe ? 'flex flex-col items-end' : ''}`}>
        {/* Name + time header */}
        {showHeader && (
          <div className={`flex items-center gap-2 mb-0.5 ${isMe ? 'flex-row-reverse' : ''}`}>
            <Link to={`/profile/${event.pubkey}`} className="flex items-center gap-1 min-w-0 hover:underline">
              <span className="text-xs font-bold truncate max-w-[150px]">{name}</span>
              <VerifiedBadge nip05={profile?.nip05} pubkey={event.pubkey} size="xs" />
            </Link>
            <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(event.created_at)}</span>
          </div>
        )}

        {/* Message bubble */}
        <div className={`relative max-w-[85%] inline-block ${isMe ? 'ml-auto' : ''}`}>
          {editing ? (
            /* Edit mode */
            <div className="rounded-2xl overflow-hidden border-2 border-primary bg-muted p-2">
              <textarea
                ref={editInputRef}
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                rows={2}
                className="w-full bg-transparent text-sm resize-none focus:outline-none min-w-[200px]"
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
                  if (e.key === 'Escape') setEditing(false);
                }}
              />
              <div className="flex items-center justify-end gap-1.5 mt-1">
                <button
                  onClick={() => setEditing(false)}
                  className="px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={!editContent.trim() || saving}
                  className="px-2.5 py-1 text-xs font-medium bg-primary text-primary-foreground rounded-lg disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className={`rounded-2xl overflow-hidden ${
                isMe
                  ? 'bg-primary text-primary-foreground rounded-tr-md'
                  : 'bg-muted rounded-tl-md'
              }`}>
                {/* Inline images */}
                {imageUrls.length > 0 && (
                  <div className={imageUrls.length > 1 ? 'grid grid-cols-2 gap-0.5' : ''}>
                    {imageUrls.map((url: string, j: number) => (
                      <img
                        key={j}
                        src={url}
                        alt=""
                        className={`w-full object-cover ${imageUrls.length > 1 ? 'max-h-32' : 'max-h-64'}`}
                        loading="lazy"
                      />
                    ))}
                  </div>
                )}
                {/* Text */}
                {textContent && (
                  <p className="px-3 py-2 text-sm whitespace-pre-wrap break-words leading-relaxed">{textContent}</p>
                )}
                {!textContent && imageUrls.length === 0 && (
                  <p className="px-3 py-2 text-sm italic text-muted-foreground">[empty message]</p>
                )}
                {/* Edited indicator */}
                {event.tags?.some((t: string[]) => t[0] === '-edit-of') && (
                  <span className="block px-3 pb-1.5 text-[9px] opacity-50">(edited)</span>
                )}
              </div>

              {/* Hover action buttons */}
              {user && (showActions || isLiked) && (
                <div className={`absolute top-0 ${isMe ? 'left-0 -translate-x-full pr-1' : 'right-0 translate-x-full pl-1'} flex items-center gap-0.5`}>
                  {!isMe && (
                    <button
                      onClick={onLike}
                      disabled={isLiked}
                      className={`p-1 rounded-full transition-colors ${
                        isLiked ? 'text-red-500' : 'text-muted-foreground/50 hover:text-red-500 hover:bg-red-500/10'
                      }`}
                      title="Like"
                    >
                      <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-current' : ''}`} />
                    </button>
                  )}
                  {!isMe && (
                    <span className="inline-flex">
                      <BCHTipButton
                        recipientPubkey={event.pubkey}
                        recipientProfile={profile}
                        eventId={event.id}
                      />
                    </span>
                  )}
                  {/* Edit own message */}
                  {isMe && textContent && (
                    <button
                      onClick={startEdit}
                      className="p-1 rounded-full text-muted-foreground/50 hover:text-primary hover:bg-primary/10 transition-colors"
                      title="Edit message"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {/* Delete own message */}
                  {isMe && (
                    <button
                      onClick={handleDelete}
                      className="p-1 rounded-full text-muted-foreground/50 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                      title="Delete message"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {isMod && !isMe && (
                    <button
                      onClick={handleHide}
                      className="p-1 rounded-full text-muted-foreground/50 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                      title="Hide message (mod)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Post Mod Actions ─────────────────────────────────────────────

function PostModActions({ community, post, onAction, onPin, onUnpin, isPinned }: {
  community: Community;
  post: any;
  onAction: () => void;
  onPin?: () => void;
  onUnpin?: () => void;
  isPinned?: boolean;
}) {
  const { publish } = useNostrPublish();
  const [removing, setRemoving] = useState(false);
  const [pinning, setPinning] = useState(false);

  const handleRemovePost = async () => {
    if (!confirm('Remove this post from the community? It will still exist on BCH Nostr.')) return;
    setRemoving(true);
    try {
      const tags = buildRemovalTags(community, post);
      await publish({
        kind: COMMUNITY_APPROVAL_KIND,
        content: '',
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });
      onAction();
    } catch (e) {
      console.error('Post removal failed:', e);
    } finally {
      setRemoving(false);
    }
  };

  const handlePin = async () => {
    if (!onPin && !onUnpin) return;
    setPinning(true);
    try {
      if (isPinned && onUnpin) {
        await onUnpin();
      } else if (onPin) {
        await onPin();
      }
    } catch {}
    setPinning(false);
  };

  return (
    <div className="flex items-center gap-2 px-4 pb-2 -mt-1">
      {/* Pin / Unpin */}
      {(onPin || onUnpin) && (
        <button
          onClick={handlePin}
          disabled={pinning}
          className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors disabled:opacity-50 ${
            isPinned
              ? 'bg-primary/10 text-primary hover:bg-primary/20'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          {pinning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pin className="w-3 h-3" />}
          {isPinned ? 'Unpin' : 'Pin'}
        </button>
      )}
      <button
        onClick={handleRemovePost}
        disabled={removing}
        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
      >
        {removing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
        Remove
      </button>
    </div>
  );
}

// ── Moderator Chip ───────────────────────────────────────────────

function ModeratorChip({ pubkey, isCreator }: { pubkey: string; isCreator: boolean }) {
  const { profile } = useNostrProfile(pubkey);
  const name = useDisplayName(pubkey, profile, pubkey.slice(0, 8));

  return (
    <Link
      to={`/profile/${pubkey}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
    >
      {profile?.picture ? (
        <img src={profile.picture} alt="" className="w-4 h-4 rounded-full object-cover" />
      ) : (
        <div className="w-4 h-4 rounded-full bg-primary/20" />
      )}
      <span className="text-xs font-medium truncate max-w-[120px]">{name}</span>
      <VerifiedBadge nip05={profile?.nip05} pubkey={pubkey} size="xs" />
      {isCreator ? <Crown className="w-3 h-3 text-amber-500" /> : <Shield className="w-3 h-3 text-primary/60" />}
    </Link>
  );
}

// ── Edit Community Modal ─────────────────────────────────────────

function EditCommunityModal({ community, onClose, onUpdated }: { community: Community; onClose: () => void; onUpdated: () => void }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const { address: userBchAddress } = useBCHAddress(user?.pubkey);

  const [name, setName] = useState(community.name);
  const [description, setDescription] = useState(community.description);
  const [rules, setRules] = useState(community.rules);
  const [communityType, setCommunityType] = useState<CommunityType>(community.type);
  const [joinFeeUsd, setJoinFeeUsd] = useState(String(community.joinFeeUsd || '1.00'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!user || !name.trim()) return;
    setSaving(true);
    setError('');

    try {
      const parsedJoinFee = communityType === 'premium' ? parseFloat(joinFeeUsd) : 0;
      const currentMods = community.moderators.filter(pk => pk !== community.creator);

      const tags = buildCommunityDefinitionTags({
        dTag: community.dTag,
        name: name.trim(),
        description: description.trim(),
        rules: rules.trim(),
        image: community.image || undefined,
        creatorPubkey: community.creator,
        moderators: currentMods,
        type: communityType,
        joinFeeUsd: parsedJoinFee > 0 ? parsedJoinFee : undefined,
        ownerAddress: community.ownerAddress || userBchAddress || '',
      });

      await publish({
        kind: COMMUNITY_DEFINITION_KIND,
        content: '',
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      onUpdated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Settings className="w-4.5 h-4.5 text-primary" /> Edit Community
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={60}
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Rules</label>
            <textarea
              value={rules}
              onChange={e => setRules(e.target.value)}
              rows={3}
              maxLength={1000}
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Type toggle */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Community Type</label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <button
                onClick={() => setCommunityType('open')}
                className={`p-2.5 rounded-xl border text-left transition-all text-xs ${
                  communityType === 'open' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                }`}
              >
                <Globe className="w-4 h-4 mb-0.5" /> Open
              </button>
              <button
                onClick={() => setCommunityType('premium')}
                className={`p-2.5 rounded-xl border text-left transition-all text-xs ${
                  communityType === 'premium' ? 'border-amber-500 bg-amber-500/5' : 'border-border hover:border-amber-500/30'
                }`}
              >
                <Lock className="w-4 h-4 mb-0.5" /> Premium
              </button>
            </div>
          </div>

          {communityType === 'premium' && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">Join Fee (USD)</label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-bold">$</span>
                <input
                  type="number"
                  min="0.50"
                  step="0.50"
                  value={joinFeeUsd}
                  onChange={e => setJoinFeeUsd(e.target.value)}
                  className="w-full pl-7 pr-3 py-2.5 bg-muted rounded-lg text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          )}

          {error && <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}

          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

